"use client";

import { cn } from "@/lib/utils";
import useTranslate from "@app/_hooks/useTranslate";
import useAddWishlistItemTag from "@features/account/hooks/useAddWishlistItemTag";
import useAddWishlistTag from "@features/account/hooks/useAddWishlistTag";
import useDeleteWishlistItemTag from "@features/account/hooks/useDeleteWishlistItemTag";
import useWishlistTags from "@features/account/hooks/useWishlistTags";
import type { WishlistItem } from "@repo/core/wishlist";
import { Button } from "@ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@ui/field";
import { Input } from "@ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover";
import { CheckIcon, Loader2, PlusIcon } from "lucide-react";
import { FormEvent, useState, type MouseEvent } from "react";

type Props = {
  item: WishlistItem;
};

export default function WishlistItemTagMenu({ item }: Props) {
  const t = useTranslate();
  const [dialog, setDialog] = useState(false);
  const { data: wishlistTags } = useWishlistTags();
  const { addWishlistItemTag } = useAddWishlistItemTag();
  const { deleteWishlistItemTag } = useDeleteWishlistItemTag();
  const { addWishlistTagAsync, isPending } = useAddWishlistTag();

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = new FormData(event.currentTarget).get("listName")?.toString();
    if (!name) return;
    await addWishlistTagAsync(name, item);
    setDialog(false);
  }

  function isActive(tagId?: number) {
    return Boolean(tagId && item.tags?.some((tag) => tag.id === tagId));
  }

  function onToggle(event: MouseEvent<HTMLButtonElement>, tagId?: number) {
    event.preventDefault();
    event.stopPropagation();
    if (!tagId || !item.productId) return;

    if (isActive(tagId) && item.id) {
      deleteWishlistItemTag({ tagId, wishlistItemId: item.id });
      return;
    }

    addWishlistItemTag({
      tagId,
      productId: item.productId,
      variantId: item.variantId,
    });
  }

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="ghost" size="xs">
            <PlusIcon strokeWidth={1} className="size-3.5" />
            {t("list")}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="bg-white p-1"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <ul className="flex max-h-48 flex-col overflow-y-auto">
            {wishlistTags?.map(
              (tag) =>
                tag.id && (
                  <li key={tag.id}>
                    <button
                      type="button"
                      data-active={isActive(tag.id)}
                      onClick={(event) => onToggle(event, tag.id)}
                      className="flex w-full items-center justify-between px-2 py-1.5 text-left text-sm hover:bg-gray-100"
                    >
                      <span className="truncate">{tag.name}</span>
                      {isActive(tag.id) && (
                        <CheckIcon
                          strokeWidth={1}
                          className={cn("size-4 shrink-0")}
                        />
                      )}
                    </button>
                  </li>
                ),
            )}
            <li>
              <Button
                variant="link"
                size="auto"
                onClick={() => setDialog(true)}
              >
                <PlusIcon strokeWidth={1} className="size-4" />
                {t("newList")}
              </Button>
            </li>
          </ul>
        </PopoverContent>
      </Popover>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md p-6">
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <DialogTitle>{t("newList")}</DialogTitle>
            <DialogDescription className="sr-only">
              {t("whatShouldListNameBeLabel")}
            </DialogDescription>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="listName" placeholder>
                  {t("whatShouldListNameBeLabel")}
                </FieldLabel>
                <Input
                  required
                  id="listName"
                  name="listName"
                  placeholder={t("nameOfListPlaceholder")}
                />
              </Field>
            </FieldGroup>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <Loader2 className="size-4 animate-spin" strokeWidth={2} />
              ) : (
                t("createList")
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
